import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Activity, Loader2, ChevronLeft, ChevronRight, CheckCircle, XCircle, AlertCircle, Edit, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Build a human-readable description from action + entity + details
// Backend enriches details with entity names resolved from entity_id for all log types
const getActivityDescription = (log) => {
  const d = log.details || {};
  const action = log.action;
  const entity = log.entity;

  switch (action) {
    case 'LOGIN':
      return 'Logged in';

    case 'SWITCH_ACCOUNT': {
      const to = d.to_client_name || d.to_client || '?';
      const from = d.from_client_name || d.from_client || '?';
      return `Switched from "${from}" to "${to}"`;
    }

    case 'CREATE':
      if (entity === 'Template')     return d.name        ? `Created template: ${d.name}`           : 'Created a template';
      if (entity === 'TeamMember')   return d.member_name ? `Created team member: ${d.member_name}` : 'Created a team member';
      if (entity === 'Client')       return d.client_name ? `Created client: ${d.client_name}`       : 'Created a client';
      if (entity === 'Conversation') return d.to          ? `Started conversation with ${d.to}`      : 'Started a conversation';
      return `Created ${entity}`;

    case 'UPDATE':
      if (entity === 'User'   && d.action === 'password_change')       return 'Changed own password';
      if (entity === 'User'   && d.action === 'admin_password_reset')  return 'Reset user password (admin)';
      if (entity === 'Client' && d.action === 'regenerate_webhook_token') return 'Regenerated webhook token';
      if (entity === 'TeamMember') {
        const name = d.member_name ? `${d.member_name}: ` : '';
        const changed = d.changes
          ? Object.keys(d.changes).filter(k => k !== 'updated_at').join(', ')
          : null;
        return changed ? `Updated team member ${name}(${changed})` : `Updated team member${d.member_name ? `: ${d.member_name}` : ''}`;
      }
      if (entity === 'Client') {
        const changes = d.changes || {};
        const fields = Object.keys(changes).filter(k => k !== 'updated_at');
        if (fields.includes('name')) return `Renamed client to: ${changes.name}`;
        if (fields.length) return `Updated client settings (${fields.join(', ')})`;
        return 'Updated client settings';
      }
      return `Updated ${entity}`;

    case 'EDIT':
      if (entity === 'Template') return d.name ? `Edited template: ${d.name}` : 'Edited template';
      return `Edited ${entity}`;

    case 'DELETE':
      if (entity === 'Template')   return d.name        ? `Deleted template: ${d.name}`           : 'Deleted a template';
      if (entity === 'TeamMember') return d.member_name ? `Deleted team member: ${d.member_name}` : 'Deleted a team member';
      if (entity === 'Client')     return d.client_name ? `Deleted client: ${d.client_name}`       : 'Deleted a client';
      return `Deleted ${entity}`;

    case 'SUBMIT':
      if (entity === 'Template') return d.name ? `Submitted template for approval: ${d.name}` : 'Submitted template for WhatsApp approval';
      return 'Submitted for approval';

    case 'SYNC':
      if (d.total_from_wa !== undefined)
        return `Synced templates — ${d.total_from_wa} total (${d.added || 0} new, ${d.updated || 0} updated)`;
      return 'Synced templates';

    case 'SEND_MESSAGE':
      return d.to ? `Sent message to ${d.to}` : 'Sent a message';

    case 'CREATE_CONVERSATION':
      if (d.to && d.template) return `Started conversation with ${d.to} using template: ${d.template}`;
      if (d.to) return `Started conversation with ${d.to}`;
      return 'Started a conversation';

    default:
      return `${action} — ${entity}`;
  }
};

const AdminActivityPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ entity: '', action: '' });

  useEffect(() => {
    fetchLogs();
  }, [pagination.page, filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 20,
        ...(filters.entity && { entity: filters.entity }),
        ...(filters.action && { action: filters.action })
      });
      
      const response = await axios.get(`${API_URL}/activity-logs/all?${params}`);
      setLogs(response.data.logs);
      setPagination({
        page: response.data.page,
        total: response.data.total,
        totalPages: response.data.total_pages
      });
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'CREATE': return <CheckCircle className="w-4 h-4 text-[#3BAB59]" />;
      case 'DELETE': return <XCircle className="w-4 h-4 text-[#EB493D]" />;
      case 'UPDATE': return <Edit className="w-4 h-4 text-[#F9BC34]" />;
      case 'SEND_MESSAGE': return <Send className="w-4 h-4 text-[#131517]" />;
      case 'LOGIN': return <CheckCircle className="w-4 h-4 text-[#3BAB59]" />;
      default: return <AlertCircle className="w-4 h-4 text-[#8A8A8B]" />;
    }
  };

  const getActionBadge = (action) => {
    const styles = {
      CREATE: 'bg-[#3BAB59]/10 text-[#3BAB59] border-[#3BAB59]/20',
      DELETE: 'bg-[#EB493D]/10 text-[#EB493D] border-[#EB493D]/20',
      UPDATE: 'bg-[#F9BC34]/10 text-[#F9BC34] border-[#F9BC34]/20',
      LOGIN: 'bg-[#3BAB59]/10 text-[#3BAB59] border-[#3BAB59]/20',
      SEND_MESSAGE: 'bg-[#131517]/10 text-[#131517] border-[#131517]/20',
      SYNC: 'bg-blue-50 text-blue-700 border-blue-200',
      SUBMIT: 'bg-purple-50 text-purple-700 border-purple-200'
    };
    return (
      <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-medium border ${styles[action] || 'bg-[#EFEFEF] text-[#595C5C]'}`}>
        {action}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-activity-page">
      {/* Header */}
      <div>
        <h1 className="text-[30px] font-semibold text-[#131517]">Activity Logs</h1>
        <p className="text-[14px] text-[#595C5C] mt-1">View all actions performed across the platform</p>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={filters.entity || 'all'} onValueChange={(v) => setFilters({ ...filters, entity: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-40 border-[#D9E2E5]">
            <SelectValue placeholder="All Entities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            <SelectItem value="Client">Client</SelectItem>
            <SelectItem value="Template">Template</SelectItem>
            <SelectItem value="Message">Message</SelectItem>
            <SelectItem value="TeamMember">Team Member</SelectItem>
            <SelectItem value="User">User</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filters.action || 'all'} onValueChange={(v) => setFilters({ ...filters, action: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-40 border-[#D9E2E5]">
            <SelectValue placeholder="All Actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="CREATE">Create</SelectItem>
            <SelectItem value="UPDATE">Update</SelectItem>
            <SelectItem value="DELETE">Delete</SelectItem>
            <SelectItem value="LOGIN">Login</SelectItem>
            <SelectItem value="SEND_MESSAGE">Send Message</SelectItem>
            <SelectItem value="SYNC">Sync</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Logs Table */}
      <Card className="border-[#D9E2E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#595C5C]" />
            </div>
          ) : logs.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-[#D9E2E5] hover:bg-transparent">
                    <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Action</TableHead>
                    <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Description</TableHead>
                    <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">User</TableHead>
                    <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Client</TableHead>
                    <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Timestamp</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} className="border-[#D9E2E5]">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getActionIcon(log.action)}
                          {getActionBadge(log.action)}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[280px]">
                        <p className="text-[13px] text-[#131517] font-medium">
                          {getActivityDescription(log)}
                        </p>
                        <p className="text-[11px] text-[#AFB0B0] mt-0.5">
                          {log.entity}{log.entity_id ? ` · #${log.entity_id.slice(0, 8)}` : ''}
                        </p>
                      </TableCell>
                      <TableCell>
                        <p className="text-[#131517]">{log.user?.name || 'Unknown'}</p>
                        <p className="text-[11px] text-[#8A8A8B]">{log.user?.email}</p>
                      </TableCell>
                      <TableCell className="text-[#595C5C]">
                        {log.client_name || <span className="text-[#AFB0B0]">-</span>}
                      </TableCell>
                      <TableCell className="text-[#595C5C] whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-[#D9E2E5]">
                <p className="text-[13px] text-[#8A8A8B]">
                  Showing {logs.length} of {pagination.total} entries
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                    disabled={pagination.page <= 1}
                    className="border-[#D9E2E5]"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-[13px] text-[#595C5C]">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                    disabled={pagination.page >= pagination.totalPages}
                    className="border-[#D9E2E5]"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 mx-auto text-[#D9E2E5] mb-3" />
              <p className="text-[14px] text-[#8A8A8B]">No activity logs found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminActivityPage;
